import { teamsService } from "@/services/teamsService";
import { toast } from "sonner";

/**
 * Generate a new team invitation link
 * @param teamId ID of the team
 * @param expiresInDays Days until the invitation expires
 * @returns The invitation code or null if error
 */
export const generateTeamInvitation = async (
  userId: string,
  teamId: string,
  expiresInDays: number = 7
): Promise<string | null> => {
  try {
    console.log("Generating team invitation for team:", teamId);

    // Validate team ID format
    if (
      !/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(
        teamId
      )
    ) {
      toast.error("Failed to generate invitation: Invalid team ID format");
      return null;
    }

    const response = await teamsService.createInvitation(teamId, {
      expiresInDays,
    });

    if (!response.success) {
      console.error("Failed to create team invitation:", response.error);
      toast.error(`Failed to generate invitation: ${response.error?.message || 'Unknown error'}`);
      return null;
    }

    console.log("Team invitation created successfully:", response.data);
    return response.data.inviteCode;
  } catch (error) {
    console.error("Exception generating team invitation:", error);
    toast.error(
      `Failed to generate invitation: ${
        error instanceof Error ? error.message : "Unknown error"
      }`
    );
    return null;
  }
};

/**
 * Create a shareable invitation URL
 * @param inviteCode Invitation code to include in the URL
 * @returns Full invitation URL
 */
export const createInvitationUrl = (inviteCode: string): string => {
  const baseUrl = window.location.origin;
  return `${baseUrl}/invite?code=${encodeURIComponent(inviteCode)}`;
};

/**
 * Check if an invitation is valid
 * @param inviteCode Invitation code to check
 * @returns Invitation info if valid, null otherwise
 */
export const checkInvitationValid = async (
  inviteCode: string
) => {
  try {
    console.log("Checking validity of invitation code:", inviteCode);

    const response = await teamsService.validateInvitation(inviteCode);

    if (!response.success) {
      console.error("Error checking invitation:", response.error);
      return null;
    }

    console.log("Invitation details:", response.data);

    if (!response.data || !response.data.valid) {
      console.log("Invitation is not valid - used or expired");
      return { valid: false };
    }

    return {
      valid: true,
      teamId: response.data.teamId,
      teamName: response.data.teamName || "Unknown Team",
      expiresAt: response.data.expiresAt,
    };
  } catch (error) {
    console.error("Exception checking invitation:", error);
    return null;
  }
};

/**
 * Redeem a team invitation
 * @param inviteCode Invitation code to redeem
 * @param userId User ID who is redeeming the invitation (no longer needed, backend gets from session)
 * @returns Success status and team info
 */
export const redeemTeamInvitation = async (
  inviteCode: string,
  userId?: string  // kept for backward compatibility but not used
) => {
  try {
    console.log("Redeeming invitation code:", inviteCode);

    const response = await teamsService.joinTeam(inviteCode);

    if (!response.success) {
      console.error("Error redeeming invitation:", response.error);
      return { 
        success: false, 
        error: response.error?.message || "Failed to redeem invitation"
      };
    }

    console.log("Invitation redemption result:", response.data);

    return {
      success: true,
      teamId: response.data.teamId,
      teamName: response.data.teamName,
      alreadyMember: response.data.alreadyMember || false,
    };
  } catch (error) {
    console.error("Exception redeeming invitation:", error);
    return {
      success: false,
      error: error instanceof Error ? error.message : "Unknown error",
    };
  }
};
